import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { useAuth, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import "./athleticTools.css";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { escapeCsvCell, escapeHtml } from "../lib/reportExport";
import { trackAdminWorkflowCompleted } from "../utils/adminPortalRollout";
import {
  BarChart3, DollarSign, Users, FileText, TrendingUp, Clock,
  Download, Calendar, RefreshCw, Mail, Plus, Trash2, Settings,
  ChevronRight, AlertCircle, CheckCircle, UserCheck,
  ClipboardList, Shield, Loader2, Edit2, Play, Pause, Send,
  CalendarClock, MailCheck, Building2, LayoutDashboard, Search, ArrowUpRight,
  Landmark, Trophy, Bookmark, Save, Share2
} from "lucide-react";

const CHART_COLORS = ['#0f766e', '#84cc16', '#14b8a6', '#f59e0b', '#dc2626', '#475569', '#0891b2', '#65a30d'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
      {label && <p className="font-medium mb-1">{label}</p>}
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color || entry.fill }}>
          {entry.name}: {typeof entry.value === 'number' && entry.name?.toLowerCase().includes('amount') ? `$${entry.value.toLocaleString()}` : entry.value?.toLocaleString?.() || entry.value}
        </p>
      ))}
    </div>
  );
};

const REPORT_CATEGORIES = [
  {
    id: "financial",
    name: "Money",
    icon: DollarSign,
    color: "text-green-600",
    bgColor: "bg-green-50",
    reports: [
      { id: "revenue-summary", name: "Registration Sales", description: "Gross registration sales by program; excludes processor reconciliation" },
      { id: "accounts-receivable", name: "Accounts Receivable Aging", description: "Outstanding registration balances grouped by age", schedulable: false },
      { id: "payout-reconciliation", name: "Payout Reconciliation", description: "Compare the Sporthouse ledger with Stripe activity, balances, and payouts", schedulable: false },
      { id: "payment-status", name: "Payment Status", description: "Breakdown of paid, pending, and overdue payments" },
      { id: "refunds", name: "Refunds Report", description: "All refunds issued with reasons" },
      { id: "promo-codes", name: "Promo Code Usage", description: "Which codes were used and discount totals" }
    ]
  },
  {
    id: "operations",
    name: "Operations",
    icon: Trophy,
    color: "text-teal-700",
    bgColor: "bg-teal-50",
    reports: [
      { id: "schedule", name: "Schedule & Game Operations", description: "Unassigned matches, conflicts, missing scores, events, and games", schedulable: false }
    ]
  },
  {
    id: "merchandise",
    name: "Addons & Uniforms",
    icon: FileText,
    color: "text-cyan-600",
    bgColor: "bg-cyan-50",
    reports: [
      { id: "addons", name: "All Addons", description: "View all addons and merchandise purchased during registration" },
      { id: "uniforms", name: "Uniform Orders", description: "Uniforms aggregated by size for fulfillment planning" }
    ]
  },
  {
    id: "registrations",
    name: "Registrations",
    icon: ClipboardList,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    reports: [
      { id: "by-program", name: "By Program", description: "Registration counts and fill rates per program" },
      { id: "trends", name: "Trends", description: "Registration activity over time" },
      { id: "waitlist", name: "Waitlist", description: "All waitlisted players across programs" },
      { id: "incomplete", name: "Incomplete", description: "Started but not completed registrations" },
      { id: "funnel", name: "Registration Funnel", description: "Started, completed, incomplete, and abandoned registrations", schedulable: false }
    ]
  },
  {
    id: "participants",
    name: "Participants",
    icon: Users,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    reports: [
      { id: "roster", name: "Roster & Eligibility", description: "Operational roster and household contact information" },
      { id: "retention", name: "Retention", description: "Returning vs new players comparison" },
      { id: "demographics", name: "Demographics", description: "Age and gender distribution" }
    ]
  },
  {
    id: "programs",
    name: "Programs",
    icon: BarChart3,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    reports: [
      { id: "capacity", name: "Capacity Utilization", description: "How full each program/division is" },
      { id: "performance", name: "Program Performance", description: "Enrollment, net collected, refunds, and fill rate by program", schedulable: false }
    ]
  },
  {
    id: "compliance",
    name: "Compliance",
    icon: Shield,
    color: "text-red-600",
    bgColor: "bg-red-50",
    reports: [
      { id: "certifications", name: "Staff Certifications", description: "Expiring and expired certifications" },
      { id: "documents", name: "Document Status", description: "Required documents collected per player" }
    ]
  }
];

const EmbeddedLayout = ({ children }) => <>{children}</>;

const ReportsHeader = () => (
  <EditorialPageHeader
    className="athletic-tools-heading"
    headingId="reports-heading"
    title="Reports & Insights"
    description="Understand performance, resolve operational gaps, and share trusted data."
  />
);

const AdminReports = ({ embedded = false, routeBase = "/admin/reports" }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const normalizedRouteBase = routeBase.replace(/\/$/, "");
  const routeParts = (location.pathname === normalizedRouteBase || location.pathname.startsWith(`${normalizedRouteBase}/`))
    ? location.pathname.slice(normalizedRouteBase.length).split("/").filter(Boolean)
    : [];
  const [selectedCategory, setSelectedCategory] = useState("financial");
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    program_id: "",
    org_id: "",
    period: "daily",
    days: 30,
  });
  const [programs, setPrograms] = useState([]);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [currentView, setCurrentView] = useState(null);
  const [managedView, setManagedView] = useState(null);
  const [viewShares, setViewShares] = useState([]);
  const [viewActivity, setViewActivity] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    report_type: "",
    frequency: "weekly",
    recipients: "",
    org_id: ""
  });
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [sendingNow, setSendingNow] = useState(null);
  const initialTab = routeParts[0] === "scheduled" ? "scheduled" : routeParts.length >= 2 ? "generate" : "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [reportSearch, setReportSearch] = useState("");

  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    if (routeParts[0] === "scheduled") {
      setActiveTab("scheduled");
      return;
    }
    if (routeParts[0] === "catalog") {
      setActiveTab("generate");
      return;
    }
    if (routeParts.length >= 2) {
      const [categoryId, reportId] = routeParts;
      const exists = REPORT_CATEGORIES.some(category => category.id === categoryId && category.reports.some(report => report.id === reportId));
      if (exists) {
        setActiveTab("generate");
        setSelectedCategory(categoryId);
        if (selectedReport?.categoryId !== categoryId || selectedReport?.reportId !== reportId) {
          generateReport(categoryId, reportId);
        }
      }
    } else {
      setActiveTab("overview");
    }
  // Route changes are the source of truth; generateReport is intentionally invoked only when the path changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const goToTab = (tab) => {
    setActiveTab(tab);
    navigate(tab === "overview" ? normalizedRouteBase : tab === "scheduled" ? `${normalizedRouteBase}/scheduled` : `${normalizedRouteBase}/catalog`);
  };

  const openReport = (categoryId, reportId) => {
    setCurrentView(null);
    navigate(`${normalizedRouteBase}/${categoryId}/${reportId}`);
  };

  useEffect(() => {
    fetchPrograms();
    fetchScheduledReports();
    fetchSavedViews();
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [token, isSuperAdmin]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/orgs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data.items || response.data || []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/programs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrograms(response.data.items || response.data || []);
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  };

  const fetchScheduledReports = async () => {
    try {
      const response = await axios.get(`${API}/reports/scheduled`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduledReports(response.data.scheduled_reports || []);
    } catch (error) {
      console.error("Error fetching scheduled reports:", error);
    }
  };

  const fetchSavedViews = async () => {
    try {
      const response = await axios.get(`${API}/reports/views`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedViews(response.data.views || []);
    } catch (error) {
      console.error("Error fetching saved report views:", error);
    }
  };

  useEffect(() => {
    const shareToken = new URLSearchParams(location.search).get("share");
    if (!shareToken || !token) return;
    axios.get(`${API}/reports/views/shared/${encodeURIComponent(shareToken)}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(({ data }) => {
      setCurrentView(data);
      setFilters(previous => ({ ...previous, ...(data.filters || {}) }));
      navigate(`${normalizedRouteBase}/${data.report_type}`, { replace: true });
      toast.success(`Opened shared view: ${data.name}`);
    }).catch(() => toast.error("This shared report link is invalid, expired, or unavailable to your account"));
  }, [location.search, navigate, normalizedRouteBase, token]);

  const saveCurrentView = async () => {
    if (!selectedReport || !reportData) return;
    const name = window.prompt("Name this saved report view");
    if (!name?.trim()) return;
    const visibility = window.confirm("Make this saved view visible to other authorized administrators in this organization?\n\nChoose Cancel to keep it private.")
      ? "organization" : "private";
    try {
      const response = await axios.post(`${API}/reports/views`, {
        name: name.trim(),
        report_type: `${selectedReport.categoryId}/${selectedReport.reportId}`,
        filters,
        org_id: reportData.org_id || filters.org_id || undefined,
        visible_columns: Array.isArray(reportData.data) && reportData.data[0] ? Object.keys(reportData.data[0]) : [],
        visibility,
        definition_version: "2026-08"
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCurrentView(response.data);
      await fetchSavedViews();
      toast.success(visibility === "organization" ? "Report view saved for the organization" : "Report view saved privately");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save this report view");
    }
  };

  const openSavedView = async (view) => {
    try {
      const response = await axios.post(`${API}/reports/views/${view.view_id}/open`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentView(response.data);
      setFilters(previous => ({ ...previous, ...(response.data.filters || {}) }));
      navigate(`${normalizedRouteBase}/${response.data.report_type}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to open this saved view");
    }
  };

  const shareCurrentView = async () => {
    if (!currentView) {
      toast.error("Save this report view before sharing it");
      return;
    }
    try {
      const response = await axios.post(`${API}/reports/views/${currentView.view_id}/shares`, { expires_in_days: 7 }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = `${window.location.origin}${normalizedRouteBase}?share=${encodeURIComponent(response.data.token)}`;
      await navigator.clipboard.writeText(url);
      toast.success("Authenticated share link copied; it expires in 7 days");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to create a secure share link");
    }
  };

  const manageSavedView = async (view) => {
    try {
      const [sharesResponse, activityResponse] = await Promise.all([
        axios.get(`${API}/reports/views/${view.view_id}/shares`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/views/${view.view_id}/activity`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setManagedView(view);
      setViewShares(sharesResponse.data.shares || []);
      setViewActivity(activityResponse.data.events || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Only the saved-view owner can manage sharing and activity");
    }
  };

  const renameSavedView = async () => {
    const name = window.prompt("Rename saved report view", managedView?.name || "");
    if (!name?.trim() || !managedView) return;
    try {
      const response = await axios.put(`${API}/reports/views/${managedView.view_id}`, { name: name.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManagedView(response.data);
      if (currentView?.view_id === response.data.view_id) setCurrentView(response.data);
      await fetchSavedViews();
      toast.success("Saved view renamed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to rename this saved view");
    }
  };

  const deleteSavedView = async () => {
    if (!managedView || !window.confirm(`Delete “${managedView.name}” and revoke all of its share links?`)) return;
    try {
      await axios.delete(`${API}/reports/views/${managedView.view_id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (currentView?.view_id === managedView.view_id) setCurrentView(null);
      setManagedView(null);
      await fetchSavedViews();
      toast.success("Saved view deleted and share links revoked");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete this saved view");
    }
  };

  const revokeShare = async (shareId) => {
    try {
      await axios.delete(`${API}/reports/views/${managedView.view_id}/shares/${shareId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewShares(previous => previous.map(share => share.share_id === shareId ? { ...share, revoked_at: new Date().toISOString() } : share));
      toast.success("Share link revoked");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to revoke this share link");
    }
  };

  const generateReport = async (categoryId, reportId) => {
    setLoading(true);
    setSelectedReport({ categoryId, reportId });
    setReportData(null);
    
    try {
      let endpoint = `/reports/${categoryId}/${reportId}`;
      let params = new URLSearchParams();
      
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.program_id) params.append("program_id", filters.program_id);
      if (isSuperAdmin && filters.org_id) params.append("org_id", filters.org_id);
      if (categoryId === "registrations" && reportId === "trends") {
        params.append("period", filters.period);
        params.append("days", filters.days);
      }
      
      const queryString = params.toString();
      if (queryString) endpoint += `?${queryString}`;
      
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(response.data);
    } catch (error) {
      toast.error("Failed to generate report");
      console.error("Error generating report:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = async () => {
    if (!reportData?.data) return;
    
    let data = reportData.data;
    
    // Handle nested data structures
    if (reportData.report_type === "demographics") {
      // Flatten demographics data
      const ageData = data.age_distribution?.map(d => ({ type: "age_group", ...d })) || [];
      const genderData = data.gender_distribution?.map(d => ({ type: "gender", ...d })) || [];
      data = [...ageData, ...genderData];
    } else if (reportData.report_type === "retention") {
      data = [data];
    }
    
    if (!Array.isArray(data)) data = [data];
    if (data.length === 0) {
      toast.error("No data to export");
      return;
    }
    
    const headers = Object.keys(data[0]).filter(h => !Array.isArray(data[0][h]) && typeof data[0][h] !== 'object');
    const csvContent = [
      headers.map(escapeCsvCell).join(","),
      ...data.map(row => headers.map(h => escapeCsvCell(row[h])).join(","))
    ].join("\n");

    if (!await recordExport("csv")) {
      toast.error("The export could not be audited. Nothing was downloaded.");
      return;
    }
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${reportData.report_type}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success("Report exported to CSV");
    trackAdminWorkflowCompleted("reports");
  };

  const exportToPDF = async () => {
    if (!reportData) return;
    
    // Create a printable HTML document
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Please allow popups to export PDF");
      return;
    }
    if (!await recordExport("pdf")) {
      printWindow.close();
      toast.error("The export could not be audited. Nothing was printed.");
      return;
    }
    
    const reportTitle = REPORT_CATEGORIES.find(c => c.id === selectedReport?.categoryId)
      ?.reports.find(r => r.id === selectedReport?.reportId)?.name || "Report";
    
    let tableHtml = '';
    let data = reportData.data;
    
    // Handle different data structures
    if (reportData.report_type === "demographics") {
      // Demographics has nested structure
      tableHtml = `
        <h3>Age Distribution</h3>
        <table>
          <tr><th>Age Group</th><th>Count</th></tr>
          ${data.age_distribution?.map(d => `<tr><td>${escapeHtml(d.group)}</td><td>${escapeHtml(d.count)}</td></tr>`).join('') || ''}
        </table>
        <h3>Gender Distribution</h3>
        <table>
          <tr><th>Gender</th><th>Count</th></tr>
          ${data.gender_distribution?.map(d => `<tr><td>${escapeHtml(d.gender)}</td><td>${escapeHtml(d.count)}</td></tr>`).join('') || ''}
        </table>
      `;
    } else if (reportData.report_type === "retention") {
      tableHtml = `
        <table>
          <tr><th>Metric</th><th>Value</th></tr>
          <tr><td>New Players</td><td>${escapeHtml(data.new_players)}</td></tr>
          <tr><td>Returning Players</td><td>${escapeHtml(data.returning_players)}</td></tr>
          <tr><td>Total Players</td><td>${escapeHtml(data.total_players)}</td></tr>
          <tr><td>Retention Rate</td><td>${escapeHtml(data.retention_rate)}%</td></tr>
        </table>
      `;
    } else if (Array.isArray(data) && data.length > 0) {
      const headers = Object.keys(data[0]).filter(h => !Array.isArray(data[0][h]) && typeof data[0][h] !== 'object');
      tableHtml = `
        <table>
          <tr>${headers.map(h => `<th>${escapeHtml(h.replace(/_/g, ' ').toUpperCase())}</th>`).join('')}</tr>
          ${data.map(row => `<tr>${headers.map(h => `<td>${escapeHtml(row[h])}</td>`).join('')}</tr>`).join('')}
        </table>
      `;
    }
    
    const summaryHtml = reportData.summary ? `
      <div class="summary">
        <h3>Summary</h3>
        ${Object.entries(reportData.summary).map(([k, v]) => 
          `<p><strong>${escapeHtml(k.replace(/_/g, ' '))}:</strong> ${escapeHtml(typeof v === 'number' && k.includes('amount') ? '$' + v.toFixed(2) : v)}</p>`
        ).join('')}
      </div>
    ` : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${escapeHtml(reportTitle)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #17211f; border-bottom: 3px solid #84cc16; padding-bottom: 10px; }
          h3 { color: #475569; margin-top: 20px; }
          .meta { color: #64748b; font-size: 14px; margin-bottom: 20px; }
          .summary { background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .summary p { margin: 5px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th { background: #0f766e; color: white; padding: 10px; text-align: left; font-size: 12px; }
          td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
          tr:nth-child(even) { background: #f8fafc; }
          @media print { 
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(reportTitle)}</h1>
        <div class="meta">
          Generated: ${new Date(reportData.generated_at).toLocaleString()}<br/>
          ${reportData.org_id ? `Organization: ${escapeHtml(organizations.find(o => o.org_id === reportData.org_id)?.name || reportData.org_id)}` : 'All Organizations'}
        </div>
        ${summaryHtml}
        ${tableHtml}
        <script>window.print(); window.close();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
    toast.success("PDF export ready");
  };

  const recordExport = async (format) => {
    try {
      await axios.post(`${API}/reports/export-events`, {
        report_type: `${selectedReport?.categoryId}/${selectedReport?.reportId}`,
        format,
        org_id: reportData?.org_id || filters.org_id || undefined
      }, { headers: { Authorization: `Bearer ${token}` } });
      return true;
    } catch (error) {
      console.error("Unable to record report export", error);
      return false;
    }
  };

  const handleCreateSchedule = async () => {
    if (!scheduleForm.report_type || !scheduleForm.recipients) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Super admin must select an organization
    if (isSuperAdmin && !scheduleForm.org_id) {
      toast.error("Please select an organization");
      return;
    }
    if (scheduleForm.report_type === "participants/roster") {
      toast.error("Roster reports contain personal information and cannot be emailed as attachments");
      return;
    }
    
    try {
      const recipients = scheduleForm.recipients.split(",").map(e => e.trim()).filter(Boolean);
      
      const payload = {
        report_type: scheduleForm.report_type,
        frequency: scheduleForm.frequency,
        recipients
      };
      
      // Add org_id for super_admin
      if (isSuperAdmin) {
        payload.org_id = scheduleForm.org_id;
      }
      
      if (editingSchedule) {
        // Update existing
        await axios.put(`${API}/reports/scheduled/${editingSchedule.schedule_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Scheduled report updated");
      } else {
        // Create new
        await axios.post(`${API}/reports/scheduled`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Scheduled report created");
      }
      
      setShowScheduleDialog(false);
      setScheduleForm({ report_type: "", frequency: "weekly", recipients: "", org_id: "" });
      setEditingSchedule(null);
      fetchScheduledReports();
    } catch (error) {
      toast.error(editingSchedule ? "Failed to update scheduled report" : "Failed to create scheduled report");
      console.error(error);
    }
  };

  const openEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleForm({
      report_type: schedule.report_type,
      frequency: schedule.frequency,
      recipients: schedule.recipients.join(", "),
      org_id: schedule.org_id || ""
    });
    setShowScheduleDialog(true);
  };

  const deleteScheduledReport = async (scheduleId) => {
    if (!window.confirm("Delete this scheduled report?")) return;
    
    try {
      await axios.delete(`${API}/reports/scheduled/${scheduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Scheduled report deleted");
      fetchScheduledReports();
    } catch (error) {
      toast.error("Failed to delete scheduled report");
    }
  };

  const sendReportNow = async (scheduleId) => {
    setSendingNow(scheduleId);
    try {
      await axios.post(`${API}/reports/scheduled/${scheduleId}/send-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Report sent successfully!");
      fetchScheduledReports();
    } catch (error) {
      toast.error("Failed to send report");
    } finally {
      setSendingNow(null);
    }
  };

  const toggleScheduleActive = async (schedule) => {
    try {
      await axios.put(`${API}/reports/scheduled/${schedule.schedule_id}`, {
        is_active: !schedule.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(schedule.is_active ? "Schedule paused" : "Schedule activated");
      fetchScheduledReports();
    } catch (error) {
      toast.error("Failed to update schedule");
    }
  };

  const getReportDisplayName = (reportType) => {
    if (!reportType) return "Unknown Report";
    const [categoryId, reportId] = reportType.split("/");
    const category = REPORT_CATEGORIES.find(c => c.id === categoryId);
    const report = category?.reports.find(r => r.id === reportId);
    return report ? `${category.name} - ${report.name}` : reportType.replace(/\//g, " - ").replace(/-/g, " ");
  };

  const getFrequencyBadgeColor = (frequency) => {
    switch (frequency) {
      case "daily": return "bg-blue-100 text-blue-700";
      case "weekly": return "bg-green-100 text-green-700";
      case "monthly": return "bg-purple-100 text-purple-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const currentCategory = REPORT_CATEGORIES.find(c => c.id === selectedCategory);

  const renderReportContent = () => {
    if (!reportData) return null;
    
    const { report_type, summary, data } = reportData;
    
    // Render summary cards
    const renderSummary = () => {
      if (!summary) return null;
      
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {Object.entries(summary).map(([key, value]) => (
            <div key={key} className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">
                {key.replace(/_/g, " ")}
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {typeof value === "number" && key.includes("amount") 
                  ? `$${value.toLocaleString()}` 
                  : typeof value === "number" && key.includes("rate")
                  ? `${value}%`
                  : value?.toLocaleString?.() || value}
              </p>
            </div>
          ))}
        </div>
      );
    };
    
    // Special rendering for demographics report
    const renderDemographics = () => {
      if (!data) return null;
      
      const ageData = data.age_distribution?.map(item => ({ name: item.group, count: item.count })) || [];
      const genderData = data.gender_distribution?.map(item => ({ name: item.gender, value: item.count })) || [];
      
      return (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h4 className="font-semibold text-slate-700 mb-4">Total Players: {data.total_players || 0}</h4>
          </div>
          {/* Charts */}
          {(ageData.length > 0 || genderData.length > 0) && (
            <div className="grid md:grid-cols-2 gap-6">
              {ageData.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-700 mb-4">Age Distribution</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={ageData} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: '#64748b' }} width={80} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {genderData.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-700 mb-4">Gender Distribution</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={genderData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {genderData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
          {/* Text data */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h4 className="font-semibold text-slate-700 mb-4">Age Distribution</h4>
              <div className="space-y-2">
                {data.age_distribution?.map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600">{item.group}</span>
                    <span className="font-medium text-slate-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h4 className="font-semibold text-slate-700 mb-4">Gender Distribution</h4>
              <div className="space-y-2">
                {data.gender_distribution?.map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600 capitalize">{item.gender}</span>
                    <span className="font-medium text-slate-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    };
    
    // Special rendering for retention report
    const renderRetention = () => {
      if (!data) return null;
      
      const retentionPieData = [
        { name: 'New Players', value: data.new_players || 0 },
        { name: 'Returning Players', value: data.returning_players || 0 }
      ].filter(d => d.value > 0);
      
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">New Players</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{data.new_players || 0}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Returning Players</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{data.returning_players || 0}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Players</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{data.total_players || 0}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-xs text-green-600 uppercase tracking-wide">Retention Rate</p>
              <p className="text-2xl font-bold text-green-700 mt-1">{data.retention_rate || 0}%</p>
            </div>
          </div>
          {retentionPieData.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h4 className="font-semibold text-slate-700 mb-4">New vs Returning Players</h4>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={retentionPieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    <Cell fill="#3b82f6" />
                    <Cell fill="#10b981" />
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      );
    };
    
    // Render data table
    const renderTable = () => {
      if (!data || (Array.isArray(data) && data.length === 0)) {
        return (
          <div className="text-center py-12 bg-slate-50 rounded-xl">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No data available for this report</p>
          </div>
        );
      }
      
      const tableData = Array.isArray(data) ? data : [data];
      if (tableData.length === 0) return null;
      
      // Filter out nested objects/arrays for table display
      const columns = Object.keys(tableData[0]).filter(k => {
        const val = tableData[0][k];
        return !k.includes("_id") && !Array.isArray(val) && typeof val !== 'object';
      });
      
      if (columns.length === 0) return null;
      
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                {columns.map(col => (
                  <th key={col} className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    {col.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                  {columns.map(col => (
                    <td key={col} className="py-3 px-4 text-sm text-slate-700">
                      {typeof row[col] === "number" && col.includes("amount")
                        ? `$${row[col].toLocaleString()}`
                        : typeof row[col] === "number" && col.includes("rate")
                        ? `${row[col]}%`
                        : Array.isArray(row[col])
                        ? row[col].join(", ")
                        : row[col] ?? "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    };
    
    // Choose rendering based on report type
    if (report_type === "demographics") {
      return <div>{renderDemographics()}</div>;
    }
    
    if (report_type === "retention") {
      return <div>{renderRetention()}</div>;
    }
    
    // Special rendering for uniforms report with "by program" breakdown
    if (report_type === "uniforms" && reportData.by_program) {
      return (
        <div>
          {renderSummary()}
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-slate-700 mb-3">Uniform Totals (All Programs)</h4>
              {renderTable()}
            </div>
            {reportData.by_program && reportData.by_program.length > 0 && (
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-700 mb-3">Breakdown by Program</h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">Program</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">Item</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">Size</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">Color</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.by_program.map((item, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-white">
                          <td className="py-3 px-4 text-sm text-slate-700">{item.program_name}</td>
                          <td className="py-3 px-4 text-sm text-slate-700">{item.item_name}</td>
                          <td className="py-3 px-4 text-sm text-slate-700">{item.size || "-"}</td>
                          <td className="py-3 px-4 text-sm text-slate-700">{item.color || "-"}</td>
                          <td className="py-3 px-4 text-sm font-medium text-slate-900">{item.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    // Smart chart rendering for generic reports
    const renderSmartChart = () => {
      const reportId = selectedReport?.reportId;
      
      if (reportId === 'revenue-summary' && Array.isArray(data) && data.length > 0) {
        const nameKey = Object.keys(data[0]).find(k => k.includes('name') || k.includes('program')) || Object.keys(data[0])[0];
        const amountKey = Object.keys(data[0]).find(k => k.includes('amount') || k.includes('revenue') || k.includes('total'));
        if (!amountKey) return null;
        return (
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-slate-700 mb-4">Revenue by Program</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey={nameKey} tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `$${v.toLocaleString()}`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey={amountKey} fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }
      
      if (reportId === 'trends' && Array.isArray(data) && data.length > 0) {
        const dateKey = Object.keys(data[0]).find(k => k.includes('date') || k.includes('period')) || Object.keys(data[0])[0];
        const countKey = Object.keys(data[0]).find(k => k.includes('count') || k.includes('registrations') || k.includes('total'));
        if (!countKey) return null;
        return (
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-slate-700 mb-4">Registration Trends</h4>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey={dateKey} tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey={countKey} stroke="#3b82f6" fill="url(#trendGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );
      }
      
      if (reportId === 'payment-status' && Array.isArray(data) && data.length > 0) {
        const statusKey = Object.keys(data[0]).find(k => k.includes('status')) || Object.keys(data[0])[0];
        const countKey = Object.keys(data[0]).find(k => k.includes('count') || k.includes('total') || k.includes('amount'));
        if (!countKey) return null;
        const pieData = data.map(d => ({ name: d[statusKey], value: d[countKey] }));
        return (
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-slate-700 mb-4">Payment Status Breakdown</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );
      }
      
      if (reportId === 'capacity' && Array.isArray(data) && data.length > 0) {
        const nameKey = Object.keys(data[0]).find(k => k.includes('name') || k.includes('program')) || Object.keys(data[0])[0];
        const fillKey = Object.keys(data[0]).find(k => k.includes('fill') || k.includes('utilization') || k.includes('percent') || k.includes('rate'));
        if (!fillKey) return null;
        return (
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-slate-700 mb-4">Capacity Utilization</h4>
            <ResponsiveContainer width="100%" height={Math.max(300, data.length * 40)}>
              <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
                <YAxis dataKey={nameKey} type="category" tick={{ fontSize: 12, fill: '#64748b' }} width={120} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey={fillKey} fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }
      
      if (reportId === 'promo-codes' && Array.isArray(data) && data.length > 0) {
        const codeKey = Object.keys(data[0]).find(k => k.includes('code') || k.includes('name')) || Object.keys(data[0])[0];
        const usageKey = Object.keys(data[0]).find(k => k.includes('usage') || k.includes('count') || k.includes('used'));
        if (!usageKey) return null;
        return (
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-slate-700 mb-4">Promo Code Usage</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey={codeKey} tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey={usageKey} fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }
      
      return null;
    };
    
    return (
      <div>
        {renderSummary()}
        {renderSmartChart()}
        {renderTable()}
      </div>
    );
  };

  return (
    <PageLayout>
      <div className="athletic-tools space-y-6" data-testid="admin-reports-page">
        <ReportsHeader />
        {/* Main workspace navigation */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => goToTab("overview")}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition-all ${activeTab === "overview" ? "bg-lime-300 text-[#17211f]" : "border border-[#d9ddd7] bg-white text-[#4c5b57] hover:bg-[#edf3ef]"}`}
              data-testid="tab-overview"
            >
              <LayoutDashboard className="h-4 w-4" /> Overview
            </button>
            <button
              onClick={() => goToTab("generate")}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition-all ${
                activeTab === "generate"
                  ? "bg-lime-300 text-[#17211f]"
                  : "border border-[#d9ddd7] bg-white text-[#4c5b57] hover:bg-[#edf3ef]"
              }`}
              data-testid="tab-generate"
            >
              <BarChart3 className="w-4 h-4" />
              Generate Reports
            </button>
            <button
              onClick={() => goToTab("scheduled")}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition-all ${
                activeTab === "scheduled"
                  ? "bg-lime-300 text-[#17211f]"
                  : "border border-[#d9ddd7] bg-white text-[#4c5b57] hover:bg-[#edf3ef]"
              }`}
              data-testid="tab-scheduled"
            >
              <CalendarClock className="w-4 h-4" />
              Scheduled Reports
              {scheduledReports.length > 0 && (
                <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === "scheduled" ? "bg-white/20" : "bg-slate-100"
                }`}>
                  {scheduledReports.length}
                </span>
              )}
            </button>
          </div>
          
          {activeTab === "scheduled" && (
            <Button
              onClick={() => {
                setEditingSchedule(null);
                setScheduleForm({ report_type: "", frequency: "weekly", recipients: "", org_id: "" });
                setShowScheduleDialog(true);
              }}
              className="rounded-full bg-[#0f766e] hover:bg-[#115e59]"
              data-testid="create-schedule-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Schedule
            </Button>
          )}
        </div>

        {activeTab === "overview" && (
          <div className="athletic-reports-overview space-y-5">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {[
                ["Registration Sales", "Gross registration activity", "financial", "revenue-summary", DollarSign],
                ["Accounts Receivable", "Aging and outstanding balances", "financial", "accounts-receivable", AlertCircle],
                ["Enrollment & Capacity", "Fill rates and remaining space", "programs", "capacity", Users],
                ["Roster & Eligibility", "Operational roster and contacts", "participants", "roster", UserCheck],
                ["Payout Reconciliation", "Ledger, Stripe activity, and deposits", "financial", "payout-reconciliation", Landmark],
                ["Schedule Operations", "Assignments, conflicts, and missing scores", "operations", "schedule", Trophy]
              ].map(([title, description, categoryId, reportId, Icon]) => (
                <button key={title} onClick={() => openReport(categoryId, reportId)} className="group rounded-[22px] border border-[#d9ddd7] bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-teal-700/30 hover:shadow-md">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#e5f4ef] text-[#0f766e]"><Icon className="h-5 w-5" /></span>
                  <h2 className="mt-5 font-['Manrope'] text-lg font-bold text-[#17211f]">{title}</h2>
                  <p className="mt-1 text-sm text-[#687570]">{description}</p>
                  <span className="mt-5 flex items-center gap-1 text-sm font-semibold text-[#0f766e]">Open report <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5" /></span>
                </button>
              ))}
            </div>
            <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
              <div className="rounded-[22px] border border-[#d9ddd7] bg-white p-6">

                <h2 className="mt-2 font-['Manrope'] text-2xl font-bold text-[#17211f]">Start with exceptions, not vanity metrics.</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-[#687570]">Review unpaid registrations, incomplete applications, waitlists, missing documents, and compliance expirations before broader performance trends.</p>
                <Button onClick={() => openReport("registrations", "incomplete")} className="mt-5 rounded-full bg-[#0f766e] hover:bg-[#115e59]">Review incomplete registrations</Button>
              </div>
              <div className="rounded-[22px] border border-[#d9ddd7] bg-[#edf3ef] p-6">
                <CalendarClock className="h-6 w-6 text-[#0f766e]" />
                <h2 className="mt-4 font-['Manrope'] text-xl font-bold text-[#17211f]">{scheduledReports.length} scheduled</h2>
                <p className="mt-1 text-sm text-[#687570]">Manage automated delivery, recipients, and next-send dates.</p>
                <button onClick={() => goToTab("scheduled")} className="mt-5 text-sm font-bold text-[#0f766e]">Manage schedules →</button>
              </div>
            </div>
          </div>
        )}

        {/* Generate Reports Tab */}
        {activeTab === "generate" && (
          <>
            <div className="relative max-w-xl">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#687570]" />
              <Input value={reportSearch} onChange={(event) => setReportSearch(event.target.value)} placeholder="Search reports" className="h-12 rounded-full border-[#d9ddd7] bg-white pl-11" />
            </div>
            {/* Category Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {REPORT_CATEGORIES.map(category => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setReportData(null);
                      setSelectedReport(null);
                      navigate(`${normalizedRouteBase}/catalog`);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${
                      selectedCategory === category.id
                        ? `${category.bgColor} ${category.color}`
                        : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {category.name}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Report Selection */}
              <div className="lg:col-span-1 space-y-4">
                <div className="rounded-2xl border border-[#d9ddd7] bg-[#edf3ef] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#0f766e]">Saved views</p>
                      <p className="mt-1 text-sm text-[#687570]">Private and organization views you can access.</p>
                    </div>
                    <Bookmark className="h-5 w-5 text-[#0f766e]" />
                  </div>
                  <div className="mt-3 space-y-2">
                    {savedViews.length === 0 ? (
                      <p className="rounded-xl bg-white/70 px-3 py-3 text-sm text-[#687570]">Save a generated report to reuse its filters.</p>
                    ) : savedViews.slice(0, 8).map(view => (
                      <div key={view.view_id} className="rounded-xl border border-[#d9ddd7] bg-white p-3">
                        <button onClick={() => openSavedView(view)} className="w-full text-left">
                          <span className="block truncate text-sm font-semibold text-[#17211f]">{view.name}</span>
                          <span className="mt-1 block text-xs text-[#687570]">{view.report_type.replace("/", " · ")} · {view.visibility}</span>
                          <span className="mt-1 block text-[11px] text-[#82908b]">Definition {view.definition_version || "current"}{view.last_opened_at ? ` · opened ${new Date(view.last_opened_at).toLocaleDateString()}` : ""}</span>
                        </button>
                        {(isSuperAdmin || view.created_by === user?.user_id) && (
                          <button onClick={() => manageSavedView(view)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#0f766e] hover:underline">
                            <Settings className="h-3 w-3" /> Manage
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Available Reports */}
                <div className="bg-white border border-slate-200 rounded-2xl p-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Available Reports</h3>
                  <div className="space-y-2">
                    {currentCategory?.reports.filter(report => `${report.name} ${report.description}`.toLowerCase().includes(reportSearch.toLowerCase())).map(report => (
                      <button
                        key={report.id}
                        onClick={() => openReport(currentCategory.id, report.id)}
                        className={`w-full text-left p-3 rounded-xl transition-all ${
                          selectedReport?.reportId === report.id
                            ? "bg-slate-900 text-white"
                            : "bg-slate-50 hover:bg-slate-100 text-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{report.name}</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                        <p className={`text-xs mt-1 ${
                          selectedReport?.reportId === report.id ? "text-slate-300" : "text-slate-500"
                        }`}>
                          {report.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Filters */}
                <div className="bg-white border border-slate-200 rounded-2xl p-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Filters</h3>
                  <div className="space-y-3">
                    {/* Organization selector for super_admin */}
                    {isSuperAdmin && (
                      <div>
                        <Label className="text-xs text-slate-600">Organization</Label>
                        <Select
                          value={filters.org_id || "all"}
                          onValueChange={(value) => setFilters({ ...filters, org_id: value === "all" ? "" : value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="All Organizations" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Organizations</SelectItem>
                            {organizations.map(org => (
                              <SelectItem key={org.org_id} value={org.org_id}>
                                {org.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-slate-600">Start Date</Label>
                      <Input
                        type="date"
                        value={filters.start_date}
                        onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">End Date</Label>
                      <Input
                        type="date"
                        value={filters.end_date}
                        onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Program</Label>
                      <Select
                        value={filters.program_id || "all"}
                        onValueChange={(value) => setFilters({ ...filters, program_id: value === "all" ? "" : value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="All Programs" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Programs</SelectItem>
                          {programs.map(prog => (
                            <SelectItem key={prog.program_id} value={prog.program_id}>
                              {prog.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {selectedCategory === "registrations" && selectedReport?.reportId === "trends" && (
                      <>
                        <div>
                          <Label className="text-xs text-slate-600">Period</Label>
                          <Select
                            value={filters.period}
                            onValueChange={(value) => setFilters({ ...filters, period: value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Days to Include</Label>
                          <Input
                            type="number"
                            value={filters.days}
                            onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) || 30 })}
                            className="mt-1"
                          />
                        </div>
                      </>
                    )}
                    
                  </div>
                </div>
              </div>

              {/* Report Display */}
              <div className="lg:col-span-2">
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                  ) : reportData ? (
                    <>
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">
                            {REPORT_CATEGORIES.find(category => category.id === selectedReport?.categoryId)
                              ?.reports.find(report => report.id === selectedReport?.reportId)?.name
                              || reportData.report_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                          </h2>
                          <p className="text-sm text-slate-500">
                            Generated {new Date(reportData.generated_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={saveCurrentView}>
                            <Save className="w-4 h-4 mr-1" /> Save view
                          </Button>
                          <Button variant="outline" size="sm" onClick={shareCurrentView}>
                            <Share2 className="w-4 h-4 mr-1" /> Share
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => generateReport(selectedReport.categoryId, selectedReport.reportId)}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={exportToCSV}
                          >
                            <Download className="w-4 h-4 mr-1" /> CSV
                          </Button>
                          <Button
                            size="sm"
                            onClick={exportToPDF}
                            className="bg-slate-900 hover:bg-slate-800"
                          >
                            <FileText className="w-4 h-4 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                      {renderReportContent()}
                      {reportData.source && (
                        <div className="mt-6 rounded-2xl border border-teal-100 bg-teal-50/70 p-4 text-sm text-[#4c5b57]">
                          <p className="font-semibold text-[#0f766e]">Data sources and limitations</p>
                          {Object.entries(reportData.source).map(([key, value]) => (
                            <p key={key} className="mt-1"><span className="font-medium capitalize">{key.replace(/_/g, " ")}:</span> {value}</p>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-20">
                      <BarChart3 className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700 mb-2">Select a Report</h3>
                      <p className="text-slate-500">
                        Choose a report from the left to generate and view data
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Scheduled Reports Tab */}
        {activeTab === "scheduled" && (
          <div className="space-y-4">
            {scheduledReports.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
                <CalendarClock className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Scheduled Reports</h3>
                <p className="text-slate-500 mb-6 max-w-md mx-auto">
                  Set up automated reports to be delivered to your inbox on a daily, weekly, or monthly basis.
                </p>
                <Button
                  onClick={() => {
                    setEditingSchedule(null);
                    setScheduleForm({ report_type: "", frequency: "weekly", recipients: "", org_id: "" });
                    setShowScheduleDialog(true);
                  }}
                  className="bg-slate-900 hover:bg-slate-800 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Schedule
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {scheduledReports.map(schedule => (
                  <div 
                    key={schedule.schedule_id} 
                    className={`bg-white border rounded-2xl p-5 transition-all ${
                      schedule.is_active !== false ? "border-slate-200" : "border-slate-200 opacity-60"
                    }`}
                    data-testid={`schedule-${schedule.schedule_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {getReportDisplayName(schedule.report_type)}
                          </h3>
                          <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full capitalize ${getFrequencyBadgeColor(schedule.frequency)}`}>
                            {schedule.frequency}
                          </span>
                          {schedule.is_active === false && (
                            <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                              Paused
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5" />
                            {schedule.recipients?.length || 0} recipient{schedule.recipients?.length !== 1 ? "s" : ""}
                          </span>
                          {schedule.next_send && (
                            <span className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5" />
                              Next: {schedule.next_send}
                            </span>
                          )}
                          {schedule.last_sent && (
                            <span className="flex items-center gap-1.5">
                              <MailCheck className="w-3.5 h-3.5 text-green-500" />
                              Last sent: {schedule.last_sent}
                            </span>
                          )}
                        </div>
                        
                        {schedule.recipients?.length > 0 && (
                          <div className="mt-2 text-xs text-slate-400">
                            {schedule.recipients.slice(0, 3).join(", ")}
                            {schedule.recipients.length > 3 && ` +${schedule.recipients.length - 3} more`}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => sendReportNow(schedule.schedule_id)}
                          disabled={sendingNow === schedule.schedule_id}
                          className="h-9 w-9 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                          title="Send now"
                        >
                          {sendingNow === schedule.schedule_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleScheduleActive(schedule)}
                          className={`h-9 w-9 p-0 ${
                            schedule.is_active !== false 
                              ? "text-slate-500 hover:text-amber-600 hover:bg-amber-50" 
                              : "text-green-500 hover:text-green-600 hover:bg-green-50"
                          }`}
                          title={schedule.is_active !== false ? "Pause schedule" : "Resume schedule"}
                        >
                          {schedule.is_active !== false ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditSchedule(schedule)}
                          className="h-9 w-9 p-0 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                          title="Edit schedule"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteScheduledReport(schedule.schedule_id)}
                          className="h-9 w-9 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
                          title="Delete schedule"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Schedule Report Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={(open) => {
        setShowScheduleDialog(open);
        if (!open) {
          setEditingSchedule(null);
          setScheduleForm({ report_type: "", frequency: "weekly", recipients: "", org_id: "" });
        }
      }}>
        <DialogContent className="max-w-md" aria-describedby="schedule-dialog-description">
          <DialogHeader>
            <DialogTitle>{editingSchedule ? "Edit Scheduled Report" : "Create Scheduled Report"}</DialogTitle>
          </DialogHeader>
          <p id="schedule-dialog-description" className="text-sm text-slate-500 -mt-2 mb-2">
            {editingSchedule 
              ? "Update the schedule settings below." 
              : "Set up automated reports to be delivered to your inbox."}
          </p>
          <div className="space-y-4 py-4">
            {/* Organization selector for super_admin */}
            {isSuperAdmin && !editingSchedule && (
              <div>
                <Label>Organization <span className="text-red-500">*</span></Label>
                <Select
                  value={scheduleForm.org_id}
                  onValueChange={(value) => setScheduleForm({ ...scheduleForm, org_id: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select an organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.org_id} value={org.org_id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Reports will be generated for this organization's data
                </p>
              </div>
            )}
            
            <div>
              <Label>Report Type <span className="text-red-500">*</span></Label>
              <Select
                value={scheduleForm.report_type}
                onValueChange={(value) => setScheduleForm({ ...scheduleForm, report_type: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a report" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_CATEGORIES.map(cat => (
                    <div key={cat.id}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                        {cat.name}
                      </div>
                      {cat.reports.filter(r => r.schedulable !== false).map(r => (
                        <SelectItem key={`${cat.id}/${r.id}`} value={`${cat.id}/${r.id}`}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Frequency</Label>
              <Select
                value={scheduleForm.frequency}
                onValueChange={(value) => setScheduleForm({ ...scheduleForm, frequency: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">
                    <div className="flex items-center gap-2">
                      <span>Daily</span>
                      <span className="text-xs text-slate-400">Every day at 8am</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="weekly">
                    <div className="flex items-center gap-2">
                      <span>Weekly</span>
                      <span className="text-xs text-slate-400">Every Monday at 8am</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="monthly">
                    <div className="flex items-center gap-2">
                      <span>Monthly</span>
                      <span className="text-xs text-slate-400">1st of each month at 8am</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Recipients <span className="text-red-500">*</span></Label>
              <Input
                placeholder="email1@example.com, email2@example.com"
                value={scheduleForm.recipients}
                onChange={(e) => setScheduleForm({ ...scheduleForm, recipients: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Separate multiple email addresses with commas
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSchedule} className="bg-slate-900 hover:bg-slate-800">
              {editingSchedule ? "Save Changes" : "Create Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(managedView)} onOpenChange={(open) => !open && setManagedView(null)}>
        <DialogContent className="max-w-2xl" aria-describedby="saved-view-management-description">
          <DialogHeader>
            <DialogTitle>{managedView?.name || "Manage saved view"}</DialogTitle>
          </DialogHeader>
          <p id="saved-view-management-description" className="text-sm text-slate-500">
            Definition {managedView?.definition_version || "current"} · {managedView?.visibility || "private"} visibility
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={renameSavedView}><Edit2 className="mr-1 h-4 w-4" /> Rename</Button>
            <Button variant="outline" size="sm" className="text-red-700" onClick={deleteSavedView}><Trash2 className="mr-1 h-4 w-4" /> Delete</Button>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900">Share links</h3>
            {viewShares.length === 0 ? <p className="text-sm text-slate-500">No share links have been created.</p> : viewShares.map(share => {
              const inactive = share.revoked_at || new Date(share.expires_at) <= new Date();
              return (
                <div key={share.share_id} className="flex items-center justify-between rounded-xl border border-slate-200 p-3 text-sm">
                  <div><p className="font-medium text-slate-800">{inactive ? "Inactive" : "Active authenticated link"}</p><p className="text-xs text-slate-500">Expires {new Date(share.expires_at).toLocaleString()}{share.last_opened_at ? ` · last opened ${new Date(share.last_opened_at).toLocaleString()}` : ""}</p></div>
                  {!inactive && <Button variant="outline" size="sm" onClick={() => revokeShare(share.share_id)}>Revoke</Button>}
                </div>
              );
            })}
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900">Recent activity</h3>
            {viewActivity.length === 0 ? <p className="text-sm text-slate-500">No activity recorded.</p> : viewActivity.slice(0, 10).map((event, index) => (
              <div key={`${event.event_type}-${event.created_at}-${index}`} className="flex justify-between border-b border-slate-100 pb-2 text-sm">
                <span className="text-slate-700">{event.event_type.replace(/_/g, " ")}</span>
                <span className="text-xs text-slate-500">{new Date(event.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminReports;
